// resources/js/core/EiWidget.ts
import type { MountedHandle, WidgetKey } from '@/types/core';
import type { Config } from '@/types/global';
import { decode, validateFor, type Validators } from '@/core/props';
import { loadModule, setInstance, deleteInstance } from '@/core/registry';

export interface EiWidgetOptions<P = any> {
    key: WidgetKey;
    config: Config;
    validators?: Validators; // optional per-key schema
    onError?: (e: unknown) => void; // optional hook for UX
    supportLiveProps?: boolean; // default true
}

export class EiWidget<P = any> {
    // Element that gets mounted on
    readonly el: HTMLElement;
    // The slug/key of the widget
    readonly key: WidgetKey;
    // Easti global config
    readonly cfg: Config;
    // Optional validators for props input
    readonly validators?: Validators;
    // A handle for the widget instance once mounted
    private mounted?: MountedHandle;
    // Props for the current instance
    private props!: P;
    // Observer for the element to track props changes
    private observer?: MutationObserver;
    private abort?: AbortController;

    constructor(el: HTMLElement, opts: EiWidgetOptions<P>) {
        this.el = el;
        this.key = opts.key;
        this.cfg = opts.config;
        this.validators = opts.validators;
        this.onError = opts.onError ?? ((e) => window.easti?.log?.error?.(`[${this.key}]`, e));
        this.supportLiveProps = opts.supportLiveProps ?? true;
    }

    onError(e: unknown) {
        /* replaced in ctor */
    }
    supportLiveProps: boolean;

    /** Read & validate props from the element. */
    readProps(): P {
        const raw = this.el.getAttribute(this.cfg.propsAttr);
        const decoded = decode(raw);
        const value = validateFor(this.key, decoded, this.validators) as P;
        return value as P;
    }

    /** SSR check - cheap heuristic: data-ssr="1" or has non-empty child nodes. */
    hasSSRMarkup(): boolean {
        if (this.cfg.ssrAttr && this.el.getAttribute(this.cfg.ssrAttr) === '1') return true;
        return this.el.firstElementChild !== null;
    }

    /** Attach a MutationObserver to watch props changes on the element. */
    startPropsObserver() {
        if (!this.supportLiveProps || this.observer) return;
        this.observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.type === 'attributes' && m.attributeName === this.cfg.propsAttr) {
                    const next = this.readProps();
                    this.update(next);
                }
            }
        });
        this.observer.observe(this.el, { attributes: true, attributeFilter: [this.cfg.propsAttr] });
    }

    stopPropsObserver() {
        this.observer?.disconnect();
        this.observer = undefined;
    }

    /** Mount or hydrate. */
    async mount() {
        // Prevent re-entry
        if (this.mounted) return;

        this.props = this.readProps();
        this.abort?.abort();
        this.abort = new AbortController();
        const { signal } = this.abort;

        try {
            const mod = await loadModule(this.key);
            if (signal.aborted) return;

            const useHydrate = typeof mod.hydrate === 'function' && this.hasSSRMarkup();
            this.mounted = useHydrate
                ? await mod.hydrate!(this.el, this.props)
                : mod.mount(this.el, this.props);

            setInstance(this.el, this);
            this.startPropsObserver();
            window.easti?.log?.info?.(`[${this.key}] mounted`, { ssr: useHydrate });
        } catch (e) {
            this.onError(e);
        }
    }

    /** Incremental props update, fallback to full remount if no patch. */
    update(next: P) {
        this.props = next;
        if (this.mounted?.patch) {
            try {
                this.mounted.patch(next);
                return;
            } catch (e) {
                this.onError(e);
            }
        }
        // Fallback: full remount
        this.unmount({ keepInstance: true }).then(() => this.mount());
    }

    /** Teardown. */
    async unmount(opts: { keepInstance?: boolean } = {}) {
        this.stopPropsObserver();
        this.abort?.abort(); // cancel pending loads
        try {
            this.mounted?.dispose?.();
        } catch (e) {
            this.onError(e);
        } finally {
            this.mounted = undefined;
            if (!opts.keepInstance) deleteInstance(this.el);
            window.easti?.log?.info?.(`[${this.key}] unmounted`);
        }
    }
}
